import React, { useEffect, useState } from 'react';
import { openingApi } from '../api/opening';
import api from '../api/client';
import ProductPicker from '../components/ProductPicker';
import { useToast } from '../hooks/useToast';

const OpeningStockLines: React.FC = () => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>(null);

  const fmt = (n: any) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const load = () => {
    setLoading(true);
    openingApi.stockLines({ search: search || undefined, limit: 200 })
      .then(r => { setRows(r.data.data || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get('/settings/locations').then(r => setLocations(r.data.data || [])).catch(() => {}); }, []); // eslint-disable-line

  const save = async () => {
    if (!edit.product_id || !edit.location_id || !(Number(edit.qty) > 0)) { showToast('Product, location and quantity are required', 'error'); return; }
    setSaving(true);
    try {
      const r = await openingApi.updateStockLine(edit.id, {
        product_id: Number(edit.product_id), location_id: Number(edit.location_id),
        qty: Number(edit.qty), unit_cost: Number(edit.unit_cost || 0),
        as_of_date: edit.as_of_date || undefined,
      });
      showToast(r.data.message);
      setEdit(null); load();
    } catch (e: any) { showToast(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const doVoid = async (r: any) => {
    const reason = window.prompt(`Remove the opening stock of ${r.product_name} at ${r.location_name}?\n\nReason (optional):`);
    if (reason === null) return;
    try {
      const res = await openingApi.voidStockLine(r.id, { reason: reason || null });
      showToast(res.data.message); load();
    } catch (e: any) { showToast(e.response?.data?.message || 'Void failed', 'error'); }
  };

  const th: React.CSSProperties = { padding: '8px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase' };

  if (edit) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Correct Opening Stock</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setEdit(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save correction'}</button>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="form-group"><label className="form-label">Product *</label>
            <ProductPicker value={edit.product_id} initialLabel={edit.product_name} onSelect={(p: any) => setEdit((e: any) => ({ ...e, product_id: String(p.id), product_name: p.name }))} />
          </div>
          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Location *</label>
              <select className="form-control" value={edit.location_id} onChange={e => setEdit((x: any) => ({ ...x, location_id: e.target.value }))}>
                <option value="">Select location</option>
                {locations.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">As of date</label>
              <input type="date" className="form-control" value={edit.as_of_date || ''} onChange={e => setEdit((x: any) => ({ ...x, as_of_date: e.target.value }))} />
            </div>
            <div className="form-group"><label className="form-label">Quantity *</label>
              <input className="form-control mono" style={{ textAlign: 'right' }} value={edit.qty} onChange={e => setEdit((x: any) => ({ ...x, qty: e.target.value }))} />
            </div>
            <div className="form-group"><label className="form-label">Unit cost *</label>
              <input className="form-control mono" style={{ textAlign: 'right' }} value={edit.unit_cost} onChange={e => setEdit((x: any) => ({ ...x, unit_cost: e.target.value }))} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx3)' }}>
            Value: <strong style={{ fontFamily: 'monospace' }}>{fmt(Number(edit.qty || 0) * Number(edit.unit_cost || 0))}</strong>
            &nbsp;·&nbsp; was {fmt(Number(edit.orig_qty) * Number(edit.orig_cost))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Opening Stock Lines</h1></div>
      <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 12 }}>
        Correct or remove a line from the opening stock import. Sales and transfers that already used the stock are re-costed automatically.
      </div>
      <div className="filter-bar" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input className="form-control" style={{ maxWidth: 320 }} placeholder="Search product or SKU..." value={search}
          onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} />
        <button className="btn btn-secondary btn-sm" onClick={load}>Search</button>
        <span className="record-count" style={{ marginLeft: 'auto' }}>{rows.length} of {total}</span>
      </div>
      <div className="card">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: 'var(--sur2)' }}>
            <th style={{ ...th, textAlign: 'left' }}>SKU</th>
            <th style={{ ...th, textAlign: 'left' }}>Product</th>
            <th style={{ ...th, textAlign: 'left' }}>Location</th>
            <th style={{ ...th, textAlign: 'right' }}>Qty</th>
            <th style={{ ...th, textAlign: 'right' }}>Unit cost</th>
            <th style={{ ...th, textAlign: 'right' }}>Value</th>
            <th style={{ ...th, textAlign: 'left' }}>Date</th>
            <th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--tx3)' }}>Loading...</td></tr>
            : rows.length === 0 ? <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--tx3)' }}>No opening stock lines.</td></tr>
            : rows.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--bdr)' }}>
                <td className="td-code" style={{ padding: '8px 10px' }}>{r.sku}</td>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>{r.product_name}</td>
                <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--tx3)' }}>{r.location_name}</td>
                <td className="td-num" style={{ padding: '8px 10px' }}>{fmt(r.qty)}</td>
                <td className="td-num" style={{ padding: '8px 10px' }}>{Number(r.unit_cost).toFixed(4)}</td>
                <td className="td-num" style={{ padding: '8px 10px' }}>{fmt(r.total_cost_impact)}</td>
                <td style={{ padding: '8px 10px', fontSize: 11, color: 'var(--tx3)' }}>{r.as_of_date || '-'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEdit({ ...r, product_id: String(r.product_id), location_id: String(r.location_id), qty: String(Number(r.qty)), unit_cost: String(Number(r.unit_cost)), orig_qty: r.qty, orig_cost: r.unit_cost })}>Correct</button>
                  <button className="btn btn-secondary btn-sm" style={{ marginLeft: 6 }} onClick={() => doVoid(r)}>Void</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OpeningStockLines;
